import React from 'react';
import { TariffRule } from '../../types/model';

interface TariffOverrideTableProps {
  rules: TariffRule[];
  onChange: (rules: TariffRule[]) => void;
  countries: string[];
  sectors: string[];
  hasEoraSectorGroups?: boolean;
}

const ALL = 'All';
const TRADABLE_SECTORS = 'Tradable Sectors';
const NONTRADABLE_SECTORS = 'Non-Tradable Sectors';

const TariffOverrideTable: React.FC<TariffOverrideTableProps> = ({
  rules,
  onChange,
  countries,
  sectors,
  hasEoraSectorGroups = false,
}) => {
  const updateRow = (index: number, field: keyof TariffRule, value: string | number) => {
    const next = rules.map((row, i) =>
      i === index ? { ...row, [field]: value } : row
    );
    onChange(next);
  };

  const deleteRow = (index: number) => {
    onChange(rules.filter((_, i) => i !== index));
  };

  const addRow = () => {
    onChange([
      ...rules,
      { importer: ALL, exporter: ALL, sector: ALL, rate: 0, mode: 'Increment' },
    ]);
  };

  return (
    <div>
      {rules.length > 0 && (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Importer</th>
                <th style={styles.th}>Exporter</th>
                <th style={styles.th}>Sector</th>
                <th style={{ ...styles.th, width: '70px' }}>Rate %</th>
                <th style={{ ...styles.th, width: '100px' }}>Mode</th>
                <th style={{ ...styles.th, width: '32px' }}></th>
              </tr>
            </thead>
            <tbody>
              {rules.map((row, i) => (
                <tr key={i}>
                  <td style={styles.td}>
                    <select
                      aria-label={`Rule ${i + 1} importer`}
                      value={row.importer}
                      onChange={(e) => updateRow(i, 'importer', e.target.value)}
                      style={styles.cellSelect}
                    >
                      <option value={ALL}>All</option>
                      {countries.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </td>
                  <td style={styles.td}>
                    <select
                      aria-label={`Rule ${i + 1} exporter`}
                      value={row.exporter}
                      onChange={(e) => updateRow(i, 'exporter', e.target.value)}
                      style={styles.cellSelect}
                    >
                      <option value={ALL}>All</option>
                      {countries.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </td>
                  <td style={styles.td}>
                    <select
                      aria-label={`Rule ${i + 1} sector`}
                      value={row.sector}
                      onChange={(e) => updateRow(i, 'sector', e.target.value)}
                      style={styles.cellSelect}
                    >
                      <optgroup label="Wildcards">
                        <option value={ALL}>All</option>
                        {hasEoraSectorGroups && <option value={TRADABLE_SECTORS}>Tradable Sectors</option>}
                        {hasEoraSectorGroups && <option value={NONTRADABLE_SECTORS}>Non-Tradable Sectors</option>}
                      </optgroup>
                      <optgroup label="Individual">
                        {sectors.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </optgroup>
                    </select>
                  </td>
                  <td style={styles.td}>
                    <input
                      type="number"
                      aria-label={`Rule ${i + 1} rate`}
                      value={row.rate}
                      onChange={(e) => updateRow(i, 'rate', Number(e.target.value))}
                      style={styles.cellInput}
                    />
                  </td>
                  <td style={styles.td}>
                    <select
                      aria-label={`Rule ${i + 1} mode`}
                      value={row.mode}
                      onChange={(e) => updateRow(i, 'mode', e.target.value)}
                      style={styles.cellSelect}
                    >
                      <option value="Increment">Increment</option>
                      <option value="Set Level">Set Level</option>
                    </select>
                  </td>
                  <td style={styles.td}>
                    <button
                      onClick={() => deleteRow(i)}
                      style={styles.deleteBtn}
                      title="Remove rule"
                    >
                      &times;
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <button onClick={addRow} className="btn-outline btn-sm" style={{ marginTop: '8px' }}>
        + Add rule
      </button>
      {rules.length > 0 && (
        <div style={styles.hint}>
          More specific rules (fewer "All" fields) replace broader ones — rates are not additive.
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  tableWrapper: {
    overflowX: 'auto',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '12px',
  },
  th: {
    textAlign: 'left',
    padding: '6px 4px',
    borderBottom: '1px solid var(--color-border)',
    backgroundColor: 'var(--color-bg, #f9fafb)',
    fontWeight: 600,
    fontSize: '12px',
    color: 'var(--color-text-subtle)',
  },
  td: {
    padding: '4px',
    borderBottom: '1px solid var(--color-border)',
  },
  cellSelect: {
    width: '100%',
    padding: '4px 2px',
    fontSize: '12px',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    backgroundColor: 'white',
  },
  cellInput: {
    width: '100%',
    padding: '4px',
    fontSize: '12px',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    textAlign: 'right',
  },
  deleteBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    color: '#dc2626',
    padding: '2px 6px',
    lineHeight: 1,
  },
  hint: {
    fontSize: '12px',
    color: 'var(--color-text-subtle)',
    fontStyle: 'italic',
    marginTop: '6px',
  },
};

export default TariffOverrideTable;
